/**
 * The "In Our DNA" section on the careers pages: a heading and image cards that link to Training &
 * Development and Ryerson Gives Back. On desktop each card shows its title over the photo, and
 * hover fades the photo to reveal the description; on mobile the cards stack with the photo above
 * the text.
 *
 * Position: rendered inside the `AllSections` list of every page under `pages/about-us/careers/`
 * that uses it (`index.tsx`, `working-at-ryerson/`, `grow-with-sales/`, `grow-with-sales/academy/`,
 * `innovate-with-operations/`, `strategize-with-finance-hr-it/`, `opportunities-for-veterans/`,
 * `working-at-ryerson/training-and-development/`), titled with `title` from the same copy the
 * section renders. Only the Training & Development page passes `content`: its copy
 * (`content/ryerson/about-us/careers/working-at-ryerson/training-and-development/index.ts` →
 * `InOurDNAContent`) drops the card that links to that page. The others use `./Content.tsx` →
 * `InOurDNAContent`.
 *
 * Related: `./Desktop/InOurDNADesktop.tsx` (hover cards, card width by count),
 * `./Mobile/InOurDNAMobile.tsx`.
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import { Media } from "@ryerson/frontend.next";
import { InOurDNAContent } from "./Content";
import InOurDNADesktop from "./Desktop/InOurDNADesktop";
import InOurDNAMobile from "./Mobile/InOurDNAMobile";

/** Props the desktop and mobile layouts receive, with `content` already resolved. */
export interface InOurDNAComponentProps {
	content: InOurDNAContentProps;
	/** `ContentSection` background type. Read by the desktop layout only. */
	background?: "primary" | "secondary" | "tertiary";
}
/** Props of {@link InOurDNA}. */
export interface InOurDNAProps {
	/**
	 * Copy override. When absent, `./Content.tsx` → `InOurDNAContent` in the visitor's language.
	 */
	content?: InOurDNAContentProps;
	/** `ContentSection` background type on desktop. Default `"secondary"`. */
	background?: "primary" | "secondary" | "tertiary";
}
/** One card. */
export interface DNACard {
	title: string;
	/** The card's description text. */
	context: string;
	/** Photo path under `public/`, used as the desktop card background and the mobile image. */
	backgroundUrl: string;
	/** Internal path the card links to. */
	link: string;
}

/** Copy for one language of the section. */
export interface InOurDNAContentProps {
	/**
	 * Section heading, shown on desktop only; on mobile the `AllSections` accordion tab shows the
	 * title.
	 */
	title: string;
	/** On desktop, one card renders 1160px wide and two or more render 400px each. */
	cards: DNACard[];
}

/** The "In Our DNA" section, in a mobile and a desktop layout switched by `Media` at `lg`. */
const InOurDNA: React.FC<InOurDNAProps> = ({ content, background = "secondary" }) => {
	const {
		localization: { language },
	} = useApplication();
	if (!content) {
		content = InOurDNAContent[language];
	}
	return (
		<>
			<Media lessThan="lg">
				<InOurDNAMobile content={content} />
			</Media>
			<Media greaterThanOrEqual="lg">
				<InOurDNADesktop content={content} background={background} />
			</Media>
		</>
	);
};

export default InOurDNA;
